import { Text } from '@/components/ui/text';
import { useToastStore } from '@/lib/useToastStore';
import * as React from 'react';
import { Animated, Easing, Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export function ToastViewport() {
  const toast = useToastStore((state) => state.toast);
  const dismissToast = useToastStore((state) => state.dismissToast);
  const insets = useSafeAreaInsets();
  const [visibleToast, setVisibleToast] = React.useState(toast);
  const visibleToastRef = React.useRef(toast);
  const opacity = React.useRef(new Animated.Value(0)).current;
  const translateY = React.useRef(new Animated.Value(20)).current;

  React.useEffect(() => {
    if (toast) {
      visibleToastRef.current = toast;
      setVisibleToast(toast);
      opacity.stopAnimation();
      translateY.stopAnimation();
      opacity.setValue(0);
      translateY.setValue(20);
      Animated.parallel([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 220,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(translateY, {
          toValue: 0,
          duration: 220,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
      ]).start();
      return;
    }

    if (!visibleToastRef.current) {
      return;
    }

    opacity.stopAnimation();
    Animated.timing(opacity, {
      toValue: 0,
      duration: 160,
      easing: Easing.out(Easing.ease),
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished) {
        visibleToastRef.current = null;
        setVisibleToast(null);
      }
    });
  }, [opacity, toast, translateY]);

  if (!visibleToast) {
    return null;
  }

  return (
    <View pointerEvents="box-none" className="absolute inset-0 z-50">
      <Animated.View
        pointerEvents="box-none"
        className="absolute inset-x-4 items-center"
        style={{
          bottom: Math.max(insets.bottom + 88, 104),
          opacity,
          transform: [{ translateY }],
        }}>
        <View className="w-full max-w-[420px] flex-row items-center gap-3 rounded-[18px] border border-primary-edge/35 bg-card px-4 py-3 shadow-lg shadow-black/20">
          <View className="flex-1">
            <Text className="text-sm font-semibold text-foreground">
              {visibleToast.message}
            </Text>
          </View>
          {visibleToast.actionLabel && visibleToast.onAction ? (
            <Pressable
              accessibilityRole="button"
              className="rounded-full bg-accent px-3 py-2"
              onPress={() => {
                dismissToast();
                visibleToast.onAction?.();
              }}>
              <Text className="text-sm font-semibold text-accent-foreground">
                {visibleToast.actionLabel}
              </Text>
            </Pressable>
          ) : null}
        </View>
      </Animated.View>
    </View>
  );
}
